import { booleanHeader, Box, CreateHeaders, dateHeader, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, ErrorButton, HeaderSizeEnum, Icon, OrderTypesEnum, PrimaryButton, SnackBarContext, textHeader } from '@frontend-workspace/react-shared-lib';
import WebkuaTable from '../webkua-table/webkua-table';
import styles from './trial-licenses.module.scss';
import { t } from 'i18next';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { GetCurrentCompany } from '../customer-context/customer-context';
import LicenseDialog from '../customers/license-dialog/license-dialog';
import { useContext, useState } from 'react';
import { ValueFormatterParams } from 'ag-grid-community';
import { UseSignalRHook } from '../signalr-context/signalr-context';
import { text } from 'stream/consumers';

/* eslint-disable-next-line */
export interface TrialLicensesProps { }

interface ITrialLicense {
  licenseNumbers: string;
  userEmails: string;
  startTime: Date;
  endTime: Date;
  id: string;
}
export function TrialLicenses(props: TrialLicensesProps) {
  const currentCompany = GetCurrentCompany();
  const [selectedTrialId, setSelectedTrialId] = useState<string>("");
  const [stopTrialId, setStopTrialId] = useState<string>("");
  const { data, getData, isLoading }: { data: { licenses: any, existingTrials: ITrialLicense[] }, getData: () => void, isLoading: boolean } = useGetRequestNewHook(`companies/${currentCompany?.number}/trialLicenses`, {
    licenses: [],
    existingTrials: []
  });
  UseSignalRHook(getData, [OrderTypesEnum.LicenseAccessChangeOrder]);
  const closeTrialDialog = () => setSelectedTrialId("");
  const licenseHeaders = CreateHeaders([
    textHeader("licenseNumber", t('standard.licenseNumber'), HeaderSizeEnum.MEDIUM, {
      renderer: 'agGroupCellRenderer',
      cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
    }),
    textHeader("productName", t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("usersInCompany", "Tilganger i firma", HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    booleanHeader("activeTrial", t('standard.active')),
  ]);
  const extendTrialRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="contained"
        onClick={() => setSelectedTrialId(params.value)}>
        {t('standard.extendTrial')}
      </PrimaryButton>
    );
  }
  const stopTrialRenderer = (params: ValueFormatterParams) => {
    return (
      <ErrorButton
        variant="contained"
        onClick={() => setStopTrialId(params.value)}>
        {t('standard.stopTrial')}
      </ErrorButton>
    );
  };
  const selectedTrial = data.existingTrials.find(trial => trial.id === selectedTrialId);
  return (
    <>
      <WebkuaTable
        isLoading={isLoading}
        headers={CreateHeaders([
          textHeader("licenseNumbers", t('standard.licenseNumber'), HeaderSizeEnum.MEDIUM, {
            renderer: 'agGroupCellRenderer',
            cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
          }),
          textHeader("userEmails", "Tilganger i firma", HeaderSizeEnum.EXTRA_LARGE),
          textHeader("productNames", t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE),
          dateHeader("startTime", t('standard.activationDate')),
          dateHeader("endTime", t('standard.expires')),
          textHeader("id", "", HeaderSizeEnum.EXTRA_LARGE, {
            renderer: extendTrialRenderer,
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("id", "", HeaderSizeEnum.EXTRA_LARGE, {
            renderer: stopTrialRenderer,
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
        ])}
        data={data.existingTrials}
        expandComponent={UsageComponent}
        pageSize={15}
      />
      <WebkuaTable
        isLoading={isLoading}
        headers={licenseHeaders}
        expandComponent={ExpandComponent}
        data={data.licenses}
        pageSize={15}
      />
      {!!selectedTrial && <ExtendDialog trial={selectedTrial} getData={getData} onClose={closeTrialDialog} />}
      {!!stopTrialId && <StopTrialDialog trialId={stopTrialId} getData={getData} onClose={() => setStopTrialId("")} />}
    </>
  );
}
function UsageComponent(props: any) {
  const { data, isLoading } = useGetRequestNewHook(`trials/${props.data.id}/usage`, []);

  return <Box className="p-4">
    <WebkuaTable
      isLoading={isLoading}
      headers={CreateHeaders([
        textHeader("email", t('standard.email'), HeaderSizeEnum.MEDIUM),
        textHeader("name", t('standard.name'), HeaderSizeEnum.MEDIUM),
        textHeader("hours", t('standard.usedHours'), HeaderSizeEnum.MEDIUM),
      ])}
      data={data}
      pageSize={15}
    />
  </Box>
}
function StopTrialDialog(props: { trialId: string, getData: () => void, onClose: () => void }) {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();

  const handleStopTrial = () => {
    makeRequest(`licenses/action/stopTrialLicense/${props.trialId}`, 'DELETE').then(result => {
      setSnackbarMessage({ content: "Prøvelisensen er stoppet", type: "success" });
      props.getData();
      props.onClose();
    })
  };
  return (
    <Dialog open={true} maxWidth={"sm"}>
      <DialogTitle>{t('standard.stopTrial')}</DialogTitle>
      <DialogContent>
        <p>{t('standard.stopTrialText')}</p>
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleStopTrial}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <PrimaryButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </PrimaryButton>
      </DialogActions>

    </Dialog>
  );
}
function ExtendDialog(props: { trial: ITrialLicense, getData: () => void, onClose: () => void }) {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();

  const [newTrialEndDate, setNewTrialEndDate] = useState<Date>(props.trial.endTime);
  const handleExtendTrial = () => {
    makeRequest(`licenses/action/extendTrialLicense`, 'PUT', {
      trialId: props.trial.id,
      newEndDate: newTrialEndDate
    }).then(result => {
      setSnackbarMessage({ content: "Prøvelisensen er forlenget", type: "success" });
      props.getData();
      props.onClose();
    })
  };
  return (
    <Dialog open={true} maxWidth={"sm"}>
      <DialogTitle>{t('standard.extendTrial')}</DialogTitle>
      <DialogContent>
        <DatePicker
          inputFormat="dd.MM.yyyy"
          label={t('standard.executionDate')}
          value={newTrialEndDate}
          onChange={(newDateValue: any) => {
            setNewTrialEndDate(newDateValue);
          }}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleExtendTrial}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <PrimaryButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t('standard.cancel')}
        </PrimaryButton>
      </DialogActions>

    </Dialog>
  );
}
function ExpandComponent(props: any) {
  const currentCompany = GetCurrentCompany();
  const [licenseAccessOpen, setLicenseAccessOpen] = useState<boolean>(false);


  const { data, isLoading } = useGetRequestNewHook(`companies/${currentCompany?.number}/trialLicenses/${props.data.licenseNumber}`, []);
  const closeLicenseDialog = () => setLicenseAccessOpen(false);
  const openLicenseDialog = () => setLicenseAccessOpen(true);
  return (
    <>
      <Box className="p-4">
        <div className='me-4 my-2'>
          <PrimaryButton
            variant="contained"
            onClick={openLicenseDialog}>
            <Icon className="me-1" fontSize="small">add</Icon>{t('standard.assignLicenseRights')}
          </PrimaryButton>
        </div>
        <WebkuaTable
          isLoading={isLoading}
          headers={CreateHeaders([
            textHeader("email", t('standard.email'), HeaderSizeEnum.MEDIUM),
            textHeader("firstName", t('standard.name'), HeaderSizeEnum.MEDIUM),
            textHeader("lastName", t('standard.role'), HeaderSizeEnum.MEDIUM),
            booleanHeader("hasAccess", t('standard.active')),
            dateHeader("accessStart", t('standard.created')),
            dateHeader("accessEnd", t('standard.expires')),
          ])}
          data={data}
          pageSize={15}
        />
      </Box>
      {!!licenseAccessOpen &&
        <LicenseDialog
          open={!!licenseAccessOpen}
          licenseNumber={props.data.licenseNumber}
          onClose={closeLicenseDialog}
          initialPersonsOnLicense={data}
          maxSeats={props.data.numberOfSeats}
          isNamedUser={false}
          isTrialLicense={true}
        />}
    </>
  );
}

export default TrialLicenses;
